import { createRouter, createWebHistory } from "vue-router";
import SideMenu from "../layouts/SideMenu/SideMenu.vue";
import HomeDesktop from "@/pages/Home/HomeDesktop.vue";
import Login from "../pages/Login/Login.vue";
import Registro from "../pages/Registro/Registro.vue";
import Usuarios from "../pages/Usuarios/Usuarios.vue";
import Pedidos from "../pages/Pedidos/Pedidos.vue";
import { userUserStore } from "../stores/user";
import PedidosLive from "../pages/PedidosLive/PedidosLive.vue";
import OpcionesGenerales from "../pages/OpcionesGenerales/OpcionesGenerales.vue";
import Mesas from "../pages/Mesas/Mesas.vue";
import Turnos from "../pages/Turnos/Turnos.vue";
import TopMenu from "../layouts/TopMenu/TopMenu.vue";
import SoporteAtiendo from "../pages/SoporteAtiendo/SoporteAtiendo.vue";
import { puedeAccederModulo, isAdmin } from "../utils/permisos";
import { Permisos } from "@fixeduy/restaurantestypes";
import Estadisticas from "../pages/Estadisticas/Estadisticas.vue";
import Productos from "../pages/Productos/Productos.vue";
import RecibirTokenOAuthGoogleLectorMails from "../pages/RecibirTokenOAuthGoogleLectorMails/RecibirTokenOAuthGoogleLectorMails.vue";
import RecibirTokenOAuthGoogle from "../pages/RecibirTokenOAuthGoogle/RecibirTokenOAuthGoogle.vue";
import LogAcciones from "../pages/LogAcciones/LogAcciones.vue";
import { puedeVerLogAccionesInterno } from "../utils/permisos";
import {
  esProduccion,
  redirigirAccesoAtiendo
} from "../constants/accesoAtiendo";

const routes = [
  {
    path: "/",
    name: "inicio",
    component: TopMenu,
    // component: SideMenu,
    meta: {
      title: "Home"
    },
    children: [
      {
        path: "",
        name: "home",
        component: HomeDesktop,
        meta: { soloAdmin: true }
      },
      {
        path: "usuarios",
        children: [
          {
            path: "",
            name: "usuarios",
            component: Usuarios,
            meta: { soloAdmin: true }
          }
        ]
      },
      {
        path: "pedidos",
        children: [
          {
            path: "/pedidos",
            name: "pedidos",
            component: Pedidos,
            meta: { permiso: Permisos.Pedidos }
          }
        ]
      },
      {
        path: "pedidosLive",
        children: [
          {
            path: "/pedidosLive",
            name: "pedidosLive",
            component: PedidosLive,
            meta: { permiso: Permisos.Comandas }
          }
        ]
      },
      {
        path: "mesas",
        children: [
          {
            path: "/mesas",
            name: "mesas",
            component: Mesas,
            meta: { soloAdmin: true }
          }
        ]
      },
      {
        path: "turnos",
        children: [
          {
            path: "/turnos",
            name: "turnos",
            component: Turnos,
            meta: { permiso: Permisos.Turnos }
          }
        ]
      },
      {
        path: "productos",
        children: [
          {
            path: "/productos",
            name: "productos",
            component: Productos,
            meta: { soloAdmin: true }
          }
        ]
      },
      {
        path: "categorias",
        children: [
          {
            path: "/categorias",
            name: "categorias",
            component: Productos,
            props: { initialTab: "categorias" },
            meta: { soloAdmin: true }
          }
        ]
      },
      {
        path: "estadisticas",
        children: [
          {
            path: "/estadisticas",
            name: "estadisticas",
            component: Estadisticas
            // meta: { permiso: Permisos.Es }
          }
        ]
      },
      {
        path: "opciones",
        children: [
          {
            path: "/opciones",
            name: "opcionesGenerales",
            component: OpcionesGenerales,
            meta: { soloAdmin: true }
          }
        ]
      },
      {
        path: "logAcciones",
        children: [
          {
            path: "/logAcciones",
            name: "logAcciones",
            component: LogAcciones,
            meta: { soloMailsInternos: true }
          }
        ]
      }
    ]
  },
  {
    path: "/login",
    name: "login",
    component: Login
  },
  {
    path: "/registro",
    name: "registro",
    component: Registro
  },
  {
    path: "/soporteAtiendo/:idEmpresa",
    name: "soporteAtiendo",
    component: SoporteAtiendo,
    props: true
  },
  {
    path: "/recibirTokenOAuthGoogle",
    name: "recibirTokenOAuthGoogle",
    component: RecibirTokenOAuthGoogle
  },
  {
    path: "/recibirTokenOAuthGoogleLectorMails",
    name: "recibirTokenOAuthGoogleLectorMails",
    component: RecibirTokenOAuthGoogleLectorMails
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { left: 0, top: 0 };
  }
});

router.beforeEach(async (to, from, next) => {
  const rutasEntrada = ["login", "registro", "home", "inicio"];
  if (esProduccion && rutasEntrada.includes(to.name as string)) {
    redirigirAccesoAtiendo();
    return;
  }

  const userStore = userUserStore();
  const isLoggedIn = userStore.userLoggedIn != null;

  // Rutas públicas: login, registro, soporte
  const rutasPublicas = ["login", "registro", "soporteAtiendo"];
  if (rutasPublicas.includes(to.name as string)) {
    return next();
  }

  // Requiere login y empresa
  if (!isLoggedIn || userStore.empresa == null) {
    if (esProduccion) {
      redirigirAccesoAtiendo();
      return;
    }
    return next({ name: "login" });
  }

  // Verificar permiso de la ruta destino
  const soloAdmin = to.meta?.soloAdmin as boolean | undefined;
  const permiso = to.meta?.permiso as Permisos | undefined;

  if (soloAdmin && !isAdmin()) {
    // Redirigir al primer módulo permitido
    if (puedeAccederModulo(Permisos.Comandas))
      return next({ name: "pedidosLive" });
    if (puedeAccederModulo(Permisos.Pedidos)) return next({ name: "pedidos" });
    if (puedeAccederModulo(Permisos.Turnos)) return next({ name: "turnos" });
    if (esProduccion) {
      redirigirAccesoAtiendo();
      return;
    }
    return next({ name: "login" });
  }
  if (permiso != null && !puedeAccederModulo(permiso)) {
    if (puedeAccederModulo(Permisos.Comandas))
      return next({ name: "pedidosLive" });
    if (puedeAccederModulo(Permisos.Pedidos)) return next({ name: "pedidos" });
    if (puedeAccederModulo(Permisos.Turnos)) return next({ name: "turnos" });
    if (esProduccion) {
      redirigirAccesoAtiendo();
      return;
    }
    return next({ name: "login" });
  }

  const soloMailsInternos = to.meta?.soloMailsInternos as boolean | undefined;
  if (soloMailsInternos && !puedeVerLogAccionesInterno()) {
    if (puedeAccederModulo(Permisos.Comandas))
      return next({ name: "pedidosLive" });
    if (puedeAccederModulo(Permisos.Pedidos)) return next({ name: "pedidos" });
    if (puedeAccederModulo(Permisos.Turnos)) return next({ name: "turnos" });
    if (esProduccion) {
      redirigirAccesoAtiendo();
      return;
    }
    return next({ name: "login" });
  }

  next();
});

export default router;
